import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { uploadFile, deleteFile } from '../../services/db';

interface GalleryImage {
  id: string;
  url: string;
}

// Initial Seed Data for Migration
const initialSeedImages = [
  { id: '1', url: '/premium-project-block.png' },
  { id: '2', url: '/yapsat-realistic-1.png' },
  { id: '3', url: '/yapsat-realistic-2.png' },
  { id: '4', url: '/yapsat-realistic-3.png' },
  { id: '5', url: '/premium-project-v2.png' },
  { id: '6', url: '/premium-project-v3.png' },
  { id: '7', url: '/premium-project-v6.png' },
  { id: '8', url: '/premium-project-v4.png' },
  { id: '9', url: '/premium-project-v5.png' },
  { id: '10', url: '/premium-project-v7.png' },
  { id: '11', url: '/premium-project-v8.png' },
  { id: '12', url: '/premium-project-v9.png' },
  { id: '13', url: '/premium-project-v10.png' },
  { id: '14', url: '/premium-project-v11.png' },
  { id: '15', url: '/yapsat-1.png' },
  { id: '16', url: '/yapsat-2.png' },
  { id: '17', url: '/yapsat-3.png' },
  { id: '18', url: '/yapsat-4.png' },
  { id: '19', url: '/yapsat-5.png' },
  { id: '20', url: '/yapsat-ext-1.png' },
  { id: '21', url: '/yapsat-ext-2.png' },
  { id: '22', url: '/yapsat-ext-3.png' },
  { id: '23', url: '/yapsat-ext-4.png' },
  { id: '24', url: '/yapsat-ext-5.png' },
  { id: '25', url: '/yapsat-v4-1.png' },
  { id: '26', url: '/yapsat-v5-1.png' },
  { id: '27', url: '/yapsat-v5-2.png' },
  { id: '28', url: '/yapsat-v5-3.png' },
  { id: '29', url: '/yapsat-v5-4.png' },
  { id: '30', url: '/yapsat-v5-5.png' },
  { id: '31', url: '/yapsat-site-1.png' },
  { id: '32', url: '/yapsat-site-2.png' },
  { id: '33', url: '/yapsat-int-1.png' },
  { id: '34', url: '/yapsat-int-2.png' },
  { id: '35', url: '/yapsat-int-3.png' },
  { id: '36', url: '/yapsat-int-4.png' },
  { id: '37', url: '/yapsat-site-3.png' },
  { id: '38', url: '/yapsat-site-4.png' },
  { id: '39', url: '/yapsat-site-5.png' },
  { id: '40', url: '/yapsat-site-6.png' },
  { id: '41', url: '/yapsat-site-7.png' },
  { id: '42', url: '/yapsat-site-8.png' },
  { id: '43', url: '/yapsat-site-9.png' },
  { id: '44', url: '/yapsat-common-1.png' },
  { id: '45', url: '/yapsat-common-2.png' },
  { id: '46', url: '/yapsat-common-3.png' },
  { id: '47', url: '/yapsat-common-4.png' },
  { id: '48', url: '/yapsat-common-5.png' },
  { id: '49', url: '/tk-const-new-1.png' },
  { id: '50', url: '/tk-const-new-2.png' },
  { id: '51', url: '/tk-const-new-3.png' },
  { id: '52', url: '/tk-const-4.png' },
  { id: '53', url: '/tk-int-ref-1.png' },
  { id: '54', url: '/tk-int-1.png' },
  { id: '55', url: '/tk-int-4.png' },
  { id: '56', url: '/tk-int-5.png' },
  { id: '57', url: '/tk-int-6.png' },
  { id: '58', url: '/tk-int-8.png' },
  { id: '59', url: '/tk-int-9.png' },
  { id: '60', url: '/tk-int-10.png' },
  { id: '61', url: '/tk-int-11.png' },
  { id: '62', url: '/tk-int-12.png' },
];

export default function GalleryManager() {
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [uploadError, setUploadError] = useState('');

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const docRef = doc(db, 'pages', 'gallery');
        const snap = await getDoc(docRef);
        if (snap.exists() && snap.data().images && snap.data().images.length > 0) {
          setImages(snap.data().images);
        } else {
          // AUTO MIGRATION: SEED THE 62 HARDCODED IMAGES TO FIRESTORE
          console.log("Database empty. Seeding 62 original images to Firestore...");
          await setDoc(docRef, { images: initialSeedImages, updatedAt: new Date() }, { merge: true });
          setImages(initialSeedImages);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchGallery();
  }, []);

  const saveToFirebase = async (newImages: GalleryImage[]) => {
    try {
      const docRef = doc(db, 'pages', 'gallery');
      await setDoc(docRef, { images: newImages, updatedAt: new Date() }, { merge: true });
    } catch (err) {
      console.error('Error saving gallery state:', err);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    setUploadError('');

    const newUploadedImages: GalleryImage[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (file.size > 5 * 1024 * 1024) {
        setUploadError((prev) => prev + `\n${file.name} 5MB'dan büyük olduğu için yüklenemedi.`);
        continue;
      }

      try {
        const url = await uploadFile(file, `pages/gallery`);
        newUploadedImages.push({ id: Date.now().toString() + i, url });
      } catch (err) {
        console.error("Upload error:", err);
      }
    }

    if (newUploadedImages.length > 0) {
      const updatedImages = [...newUploadedImages, ...images];
      setImages(updatedImages);
      await saveToFirebase(updatedImages);
    }
    
    setUploading(false);
    // Reset file input
    e.target.value = '';
  };

  const handleDeleteImage = async (imageToDelete: GalleryImage) => {
    if (!window.confirm("Bu görseli galeriden kalıcı olarak silmek istediğinize emin misiniz?")) return;
    
    try {
      await deleteFile(imageToDelete.url);
      const updatedImages = images.filter(img => img.id !== imageToDelete.id);
      setImages(updatedImages);
      await saveToFirebase(updatedImages);
    } catch (err) {
      console.error(err);
      alert('Silme işlemi başarısız oldu.');
    }
  };

  if (loading) return <div className="text-zinc-500">Yükleniyor...</div>;

  return (
    <div className="space-y-6 pb-24">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8 border-b border-zinc-800 pb-6">
        <div>
          <h2 className="text-2xl font-black text-white" style={{ fontFamily: 'Raleway, sans-serif' }}>Galeri Yönetimi</h2>
          <p className="text-zinc-400 text-sm mt-1">Sitenin şantiye ve inşaat görsellerini toplu veya tekli yükleyebilirsiniz.</p>
        </div>
        
        <label className={`cursor-pointer bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-xl font-bold text-sm shadow-lg shadow-red-600/20 transition-all flex items-center gap-2 ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}>
          {uploading ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-upload-cloud-line text-lg"></i>}
          {uploading ? 'Yükleniyor...' : 'Fotoğrafları Seç & Yükle'}
          <input 
            type="file" 
            multiple 
            accept="image/*" 
            onChange={handleFileUpload} 
            className="hidden" 
            disabled={uploading} 
          />
        </label>
      </div>

      {uploadError && (
        <div className="p-4 rounded-xl bg-red-500/10 text-red-500 border border-red-500/20 whitespace-pre-line text-sm font-bold">
          {uploadError}
        </div>
      )}

      {images.length === 0 ? (
        <div className="border-2 border-dashed border-zinc-800 rounded-3xl p-16 text-center">
         <i className="ri-image-add-line text-6xl text-zinc-600 mb-6 block"></i>
         <h3 className="text-xl font-bold text-white mb-2">Galeri Şu An Boş</h3>
         <p className="text-zinc-500 max-w-sm mx-auto">
           Sağ üstteki butona tıklayarak bilgisayarınızdan aynı anda birden fazla fotoğraf seçip yükleyebilirsiniz.
         </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {images.map((img) => (
            <motion.div 
              key={img.id} 
              initial={{ opacity: 0, scale: 0.9 }} 
              animate={{ opacity: 1, scale: 1 }} 
              className="group relative aspect-square rounded-xl overflow-hidden bg-zinc-900 border border-zinc-800"
            >
              <img src={img.url} className="w-full h-full object-cover transition-transform group-hover:scale-110" alt="Gallery item" />
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <button 
                  onClick={() => handleDeleteImage(img)} 
                  className="bg-red-600 hover:bg-red-700 text-white p-3 rounded-full flex items-center justify-center transition-transform hover:scale-110"
                  title="Görseli Sil"
                >
                  <i className="ri-delete-bin-line"></i>
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
